import { useState, useEffect } from 'react';

// Define keyword-based emoji mappings per field
const fieldMappings = {
  bio: [
    { regex: /\b(surviving|struggling|hard|tough)\b/i, emojis: ['🫠','😵‍💫'] },
    { regex: /\b(awesome|great|amazing|happy|fun|excited)\b/i, emojis: ['😎','✨'] },
  ],
  aspirations: [
    { regex: /\bCEO\b/i, emojis: ['😎','🫡','🔥'] },
    { regex: /\bIAS\b/i, emojis: ['👀','🫡','😏'] },
    { regex: /\bdeveloper|engineer|founder\b/i, emojis: ['🚀','💼','😲'] },
  ],
  hobbies: [
    { regex: /\b(dance|dancing)\b/i, emojis: ['💃','😏'] },
    { regex: /\b(sing|singing)\b/i, emojis: ['🙉','🎤'] },
    { regex: /\b(read|reading)\b/i, emojis: ['👀','📚'] },
  ],
  interestsTechnical: [
    { regex: /\bc\b|c programming/i, emojis: ['💻','☕'] },
    { regex: /python/i, emojis: ['🐍','💻'] },
    { regex: /ai|ml/i, emojis: ['🤖','🧠'] },
    { regex: /web/i, emojis: ['🌐','💻'] },
  ],
  interestsNonTechnical: [
    { regex: /photo|camera|photography/i, emojis: ['📸','😏'] },
    { regex: /music|song/i, emojis: ['🎶','🙃'] },
    { regex: /art|painting/i, emojis: ['🎨','😎'] },
  ],
};

// Generic fallback emojis
const genericFallback = {
  string: ['👀','😏','🫡','🔥'],
  skills: { few: ['🤓','💻'], many: ['💪','🔥'] },
  list: { few: ['😶','🫠'], many: ['🏆','🔥'] },
  clubs: { lead: ['🫡','🚀'], other: ['😏','🎉'] },
};

/**
 * Pick up to n random emojis from an array
 */
function pickRandom(arr, n = 2) {
  const copy = [...arr];
  const result = [];
  while (result.length < n && copy.length) {
    const i = Math.floor(Math.random()*copy.length);
    result.push(copy.splice(i,1)[0]);
  }
  return result;
}

/**
 * Get emojis for a given field and its value
 * @param {string} field
 * @param {string|Array} value
 * @returns {string[]} emoji array
 */
export function getEmojis(field, value) {
  if (Array.isArray(value)) {
    // Handle array types: skills, certificates, internships, clubs
    if (field === 'skills') {
      return value.length > 3
        ? genericFallback.skills.many
        : value.length > 0
          ? genericFallback.skills.few
          : [];
    }
    if (field === 'clubs') {
      const hasLead = value.some(v => /lead|president|head|chair|organizer/i.test(v));
      return hasLead
        ? genericFallback.clubs.lead
        : (value.length > 0 ? genericFallback.clubs.other : []);
    }
    // certificates or internships
    return value.length > 2
      ? genericFallback.list.many
      : (value.length > 0 ? genericFallback.list.few : []);
  }

  const text = String(value || '').trim();
  if (!text) return [];
  const mapping = fieldMappings[field] || [];
  for (let { regex, emojis } of mapping) {
    if (regex.test(text)) {
      return pickRandom(emojis, emojis.length);
    }
  }
  // fallback generic
  return pickRandom(genericFallback.string, 2);
}

/**
 * React hook to provide emojis live as value changes
 * @param {string} field
 * @param {string|Array} value
 * @returns {string[]} emojis
 */
export default function useEmojiReaction(field, value) {
  const [emojis, setEmojis] = useState(getEmojis(field, value));
  useEffect(() => {
    setEmojis(getEmojis(field, value));
  }, [field, value]);
  return emojis;
}